/**
 * bulkFilesColumns — Column definitions and preferences for the BulkFilesPage.
 * One row per (searched file name, matching execution) pair. The file name
 * links to the File Detail page.
 */
import Badge from '@cloudscape-design/components/badge';
import Link from '@cloudscape-design/components/link';
import StatusIndicator, { StatusIndicatorProps } from '@cloudscape-design/components/status-indicator';
import { CopyableText } from 'src/components/common/CopyableText';
import { formatIsoTimestamp, formatLatency } from 'src/utils/formatters';
import { FileExecutionRow } from './filesHelpers';

/** Maps a beacon execution state to a Cloudscape StatusIndicator type. */
const stateToIndicatorType = (state: string | null): StatusIndicatorProps.Type => {
  if (state === 'SUCCESS') return 'success';
  if (state === 'FAILED') return 'error';
  return 'in-progress';
};

/** Table column definitions for file search results. */
export const FILE_ROW_COLUMN_DEFINITIONS = [
  {
    id: 'fileName',
    header: 'File name',
    cell: (row: FileExecutionRow) => (
      <Link href={`/file?name=${encodeURIComponent(row.fileName)}`} external>
        {row.fileName}
      </Link>
    ),
    sortingField: 'fileName',
  },
  {
    id: 'id',
    header: 'Execution ID',
    cell: (row: FileExecutionRow) => <CopyableText value={row.execution.id} />,
  },
  {
    id: 'executionType',
    header: 'Type',
    cell: (row: FileExecutionRow) => <Badge>{row.execution.executionType}</Badge>,
  },
  {
    id: 'beaconExecutionStatus',
    header: 'Status',
    cell: (row: FileExecutionRow) => row.execution.beaconExecutionStatus || '—',
  },
  {
    id: 'beaconExecutionState',
    header: 'State',
    cell: (row: FileExecutionRow) =>
      row.execution.beaconExecutionState ? (
        <StatusIndicator type={stateToIndicatorType(row.execution.beaconExecutionState)}>
          {row.execution.beaconExecutionState}
        </StatusIndicator>
      ) : ('—'),
  },
  {
    id: 'dataSource',
    header: 'Data source',
    cell: (row: FileExecutionRow) => row.execution.dataSource || '—',
  },
  {
    id: 'paymentIds',
    header: 'Payments',
    cell: (row: FileExecutionRow) => row.execution.paymentIds?.length ?? 0,
  },
  {
    id: 'createdAt',
    header: 'Created',
    cell: (row: FileExecutionRow) => formatIsoTimestamp(row.execution.createdAt),
  },
  {
    id: 'totalLatencyMs',
    header: 'Total latency',
    cell: (row: FileExecutionRow) => formatLatency(row.execution.totalLatencyMs),
  },
];

/** Page size for the server-side FetchFiles request. */
export const DEFAULT_PAGE_SIZE = 100;
